// Fechas en la zona de negocio (America/Mexico_City), sin depender de la zona del navegador.

export const ZONA = 'America/Mexico_City';
const DIA_MS = 86_400_000;

export interface PartesFecha {
  anio: number; mes: number; dia: number; // mes 1..12
  hora: number; minuto: number; segundo: number;
  diaSemana: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = domingo
}

const partesFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONA, hourCycle: 'h23',
  year: 'numeric', month: 'numeric', day: 'numeric',
  hour: 'numeric', minute: 'numeric', second: 'numeric', weekday: 'short',
});
const DIAS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function partesEnZona(fecha: Date | number | string): PartesFecha {
  const d = new Date(fecha);
  const p: Record<string, string> = {};
  for (const x of partesFmt.formatToParts(d)) p[x.type] = x.value;
  return {
    anio: +p.year, mes: +p.month, dia: +p.day,
    hora: +p.hour % 24, minuto: +p.minute, segundo: +p.second,
    diaSemana: DIAS_EN.indexOf(p.weekday) as PartesFecha['diaSemana'],
  };
}

/** Diferencia (ms) entre la hora de pared en CDMX y UTC en ese instante. */
function desfaseMs(instante: number): number {
  const p = partesEnZona(instante);
  const comoUtc = Date.UTC(p.anio, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo);
  return comoUtc - Math.floor(instante / 1000) * 1000;
}

/** Instante UTC (ms) de una hora de pared en CDMX. */
export function instanteDesdeZona(anio: number, mes: number, dia: number, hora = 0, minuto = 0): number {
  const supuesto = Date.UTC(anio, mes - 1, dia, hora, minuto);
  const primero = supuesto - desfaseMs(supuesto);
  // Segunda pasada por si el desfase cambia justo en ese intervalo.
  return supuesto - desfaseMs(primero);
}

/** Medianoche en CDMX del día que contiene `fecha`. */
export function inicioDelDia(fecha: Date | number | string): number {
  const p = partesEnZona(fecha);
  return instanteDesdeZona(p.anio, p.mes, p.dia);
}

export function sumarDias(instante: number, dias: number): number {
  const p = partesEnZona(instante);
  const base = new Date(Date.UTC(p.anio, p.mes - 1, p.dia + dias));
  return instanteDesdeZona(base.getUTCFullYear(), base.getUTCMonth() + 1, base.getUTCDate(), p.hora, p.minuto);
}

const dos = (n: number) => String(n).padStart(2, '0');

/** "2026-09-15" en CDMX (para <input type="date">). */
export function aFechaInput(fecha: Date | number | string): string {
  const p = partesEnZona(fecha);
  return `${p.anio}-${dos(p.mes)}-${dos(p.dia)}`;
}

/** "2026-09-15T14:30" en CDMX (para <input type="datetime-local">). */
export function aFechaHoraInput(fecha: Date | number | string): string {
  const p = partesEnZona(fecha);
  return `${aFechaInput(fecha)}T${dos(p.hora)}:${dos(p.minuto)}`;
}

/** Lee "YYYY-MM-DD" o "YYYY-MM-DDTHH:MM" como hora de CDMX y devuelve ISO UTC. */
export function desdeFechaInput(valor: string, horaPorDefecto = '00:00'): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(valor.trim());
  if (!m) return null;
  const [hh, mm] = m[4] ? [+m[4], +m[5]] : horaPorDefecto.split(':').map(Number);
  return new Date(instanteDesdeZona(+m[1], +m[2], +m[3], hh, mm)).toISOString();
}

const fmt = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('es-MX', { timeZone: ZONA, ...o });
const fFechaCorta = fmt({ weekday: 'short', day: 'numeric', month: 'short' });
const fFecha = fmt({ day: 'numeric', month: 'short', year: 'numeric' });
const fFechaHora = fmt({ day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const fHora = fmt({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const fDiaMes = fmt({ day: 'numeric', month: 'short' });

const limpiar = (s: string) => s.replace(/\./g, '').replace(/\s+/g, ' ').trim();

/** "lun 15 de sep" */
export const fechaCorta = (f: string | number | Date) => limpiar(fFechaCorta.format(new Date(f)).replace(/,/g, ''));
/** "15 sep 2026" */
export const fecha = (f: string | number | Date) => limpiar(fFecha.format(new Date(f)));
/** "15 sep, 14:30" */
export const fechaHora = (f: string | number | Date) => limpiar(fFechaHora.format(new Date(f)));
/** "14:30" */
export const hora = (f: string | number | Date) => fHora.format(new Date(f));
/** "15 sep" */
export const diaMes = (f: string | number | Date) => limpiar(fDiaMes.format(new Date(f)));

/** Días de calendario (CDMX) entre hoy y la fecha: 0 hoy, 1 mañana, -1 ayer. */
export function diasDesdeHoy(f: string | number | Date, ahora: number = Date.now()): number {
  return Math.round((inicioDelDia(f) - inicioDelDia(ahora)) / DIA_MS);
}

/** "hoy", "mañana", "ayer", "en 3 días", "hace 2 días". */
export function relativoDias(f: string | number | Date, ahora: number = Date.now()): string {
  const d = diasDesdeHoy(f, ahora);
  if (d === 0) return 'hoy';
  if (d === 1) return 'mañana';
  if (d === -1) return 'ayer';
  return d > 0 ? `en ${d} días` : `hace ${-d} días`;
}
